/**
 * Japanese translations (日本語翻訳)
 */
import type { TranslationKeys } from '../types';

export const ja: TranslationKeys = {
  common: {
    confirm: '確定',
    cancel: '取消',
    pass: 'パス',
    finish: '完了',
    close: '閉じる',
    reset: 'リセット',
    waiting: '順番を待っています...',
    loading: '読み込み中...',
  },

  game: {
    titlePrefix: '蒸気の時代',
    subtitle: 'ソロマップ',
    turn: 'ターン',
    phase: 'フェーズ',
    player: 'P1',
    bankrupt: '破産',
    gameOver: 'ゲーム終了',
  },

  phases: {
    issueShares: 'Ph.1: 株式発行',
    selectActions: 'Ph.3: アクション選択',
    buildTrack: 'Ph.4: 線路敷設',
    moveGoods: 'Ph.5: 商品輸送',
    collectIncome: 'Ph.6: 収入獲得',
    payExpenses: 'Ph.7: 経費支払',
    incomeReduction: 'Ph.8: 収入減少',
    refillGoods: 'Ph.9: 商品補充',
    advanceTurn: 'Ph.10: ターン進行',
    completed: 'ゲーム終了',
  },

  stats: {
    cash: '現金',
    income: '収入',
    expenses: '支出',
    shares: '株式',
    locoLevel: '機関車Lv.',
    cargoLevel: '貨物Lv.',
    goods: '商品',
    playerStatus: 'プレイヤー状態',
    // Mobile short labels
    cashShort: '金:',
    incomeShort: '収:',
    payShort: '払:',
    issueShort: '株:',
    locoShort: '機:',
    cargoShort: '貨:',
  },

  issueShares: {
    title: '株式発行',
    numberOfShares: '発行株数 (最大: ${max}):',
    cashReward: '+${amount} 現金',
    helpTitle: '特別ルール:',
    barbados: {
      helpMaxPerTurn: '1ターン最大1株まで',
    },
    detroit: {
      helpMaxShares: '最大25株まで',
    },
  },

  selectActions: {
    title: 'アクション選択',
    actions: {
      engineer: {
        name: 'エンジニア',
        description: '線路タイル配置4枚',
      },
      locomotive: {
        name: '機関車',
        description: '機関車レベルを無料で+1',
      },
      urbanization: {
        name: '都市化',
        description: '町ヘクスを都市化',
      },
      production: {
        name: '商品補充',
        description: '商品2個を追加',
      },
    },
    used: '(使用済)',
    helpTitle: '特別ルール:',
    helpSelectOne: '毎ターン1アクション選択（必須）',
    helpLocked: '使用済みアクションは4つ全て使うまでロック',
    helpReset: '4ターン後、全アクションがリセット',
    insufficientFunds: '資金不足（$5必要）',
    maxLevel: '最大レベルに達しています',
    puertoRico: {
      costInfo: 'アクション費用: $5',
      currentCash: '所持金',
      skip: 'スキップ（費用なし）',
      helpOptional: 'アクション選択は任意です',
      helpCost: 'アクション選択には$5かかります',
      helpLocomotive: '機関車アクションは即座に適用されます',
    },
    detroit: {
      costInfo: '残りディスク数に応じた手数料',
      currentCash: '所持金',
      skip: 'スキップ（費用なし）',
      helpOptional: 'アクション選択は任意です',
      helpCost: '3枚=$3、2枚=$2、1枚=$1',
      helpLocomotive: '各アクションは最大3回まで使用可能',
    },
    seattle: {
      locomotiveDescription: '機関車+1 または 貨物輸送+1',
      skip: 'スキップ',
      helpOptional: '1ターンに0〜2個のアクションを選択可能',
      helpCost: '選択時は無料、清算時に$5/回',
      helpLocomotive: '機関車: 機関車+1 か 貨物輸送+1 を選択',
      locoEffectTitle: '機関車効果を選択',
      locoUp: '機関車レベル +1',
      cargoUp: '貨物輸送レベル +1',
      engineerUses: '合計使用回数：エンジニア',
      locomotiveUses: '合計使用回数：機関車',
      settlementCost: 'ゲーム終了時の支払い',
    },
  },

  buildTrack: {
    title: '線路敷設',
    trackTiles: '線路タイル:',
    cityTiles: '都市:',
    remaining: '残り',
    cost: 'コスト: ${amount}',
    townDisk: '町ディスク:',
    payment: '支払い',
    overBudget: '所持金オーバー',
    validation: {
      playerBankrupt: '破産したプレイヤーは線路を建設できません',
      invalidTileType: '無効なタイルタイプ',
      maxTilesReached: '1ターンで配置できるタイル枚数上限を超えています',
      newCityLimitReached: '1ターンに配置できる新都市タイルは1枚のみです',
      newCityNotAvailable: '{type}はBarbadosマップでは利用できません (A-Dのみ)',
      hexNotFound: 'ヘクス{hex}はマップ上に存在しません',
      cannotBuildOnCity: '都市ヘクスには線路を配置できません',
      cannotBuildOnUrbanizedTown: '都市化された町ヘクスには線路を配置できません',
      townTileRequiresTownHex: 'Townタイルは町ヘクスにのみ配置可能です',
      newCityRequiresTownHex: '新都市タイルは町ヘクスにのみ配置可能です',
      urbanizationRequired: '新都市タイル配置には都市化アクションが必要です',
      urbanizationAlreadyUsed: '都市化アクションは既にこのゲームで使用済みです',
      hexOccupied: 'ヘクス{hex}は既に占有されています',
      firstTrackMustConnectCity: '最初の線路は都市に接続する必要があります',
      mustConnectToExisting: '線路は既存の線路または都市に接続する必要があります',
      floatingTrackPath: '線路パス{num}は都市や既存線路に接続されていません',
      cannotConnectMapEdge: 'タイルをマップ外に接続することはできません',
      cannotConnectSea: 'タイルを海に接続することはできません',
      insufficientFunds: '資金不足',
      selfReferencingLink: '同じ City に直接戻るような敷設はできません',
      invalidReplacementPattern: '複線タイルを単線タイルに置き換えることはできません',
      invalidReplacementConnection: '新しいタイルは既存の線路パスを含む必要があります',
      newPathNotConnected: '新しい線路パスは隣接タイルまたは都市に接続する必要があります',
      notAtUnfinishedSectionEnd: '単線→単線置き換えは未完成セクションの端でのみ許可されます',
      cannotReplaceOtherPlayerTrack: '他プレイヤーの線路を置き換えることはできません',
      replacementBreaksConnection: '新しいタイルは既存の接続を維持する必要があります',
      cannotReplaceCompletedLink: '完成リンクの一部であるタイルは置き換えできません',
      cannotModifyOtherPlayerPath: '複線置き換えで他プレイヤーの線路パスを変更することはできません',
      pathNotAtUnfinishedSectionEnd: '変更パスは未完成セクションの端にある必要があります',
      townTileLimitReached: '町には既に2枚の通常タイルが配置されています（上限: 2枚）',
      townDiskAlreadyPlaced: '町には既に通常タイルが配置されています（上限: 各町1枚）',
      noTownDisksRemaining: 'Town Disk の残りがありません（上限: 8枚）',
      noTrackToReplace: 'このヘクスには置き換える線路がありません',
      cannotBuildOnMountains: 'このマップでは山岳ヘクスに線路を配置できません',
      wallBlocksConnection: 'タイルは壁を越えて接続できません',
      townEdgeNotPreserved: '既存の接続が新しいタイルで維持されていません',
      existingEdgeNotInTown: '既存の接続が新しいタイルに含まれていません',
      existingEdgeNotPreserved: '既存の接続が新しいタイルで維持されていません',
    },
    townDisksRemaining: 'Town Disk: {count}枚',
    help: {
      title: '操作説明',
      placeTile: 'タイルを選択し、マップ上のヘクスをクリック。クリックするたび回転。確定ボタンで配置。',
      cancelTile: '取消ボタンでこのターンの配置を取消。',
      finishBuild: '完了でコストを支払い次のフェーズへ。',
      specialLink: '隣接リンク、フェリーリンクはコスト表示部をクリックして配置。',
    },
  },

  moveGoods: {
    title: '商品輸送',
    selectGoods: '輸送する商品を選択',
    guide: 'クリックのたび複数経路切替',
    transportable: '輸送可能 ({count}):',
    noGoodsAvailable: '輸送可能な商品がありません',
    selectRoute: '経路選択:',
    route: '経路:',
    links: '{count}リンク',
    clickToChange: 'クリックで経路変更',
    link: '{count}リンク',
    upgradeLoco: '機関車強化',
    maxLocoLevel: '機関車Lv.最大',
    confirmUpgrade: '機関車を強化しますか？',
    validation: {
      invalidPhase: '現在のフェーズは{phase}です。商品輸送が必要です',
      playerBankrupt: '破産したプレイヤーは商品を輸送できません',
      cityNotFound: '都市{city}が見つかりません',
      goodsNotFound: '商品{color}が都市{city}に見つかりません',
      destinationColorMismatch: '都市{city}の色は{actual}ですが、{expected}が必要です',
      locoAlreadyUpgraded: 'このターンで既に機関車を強化済みです',
      maxLocoLevel: '機関車レベルは既に最大 (6) です',
    },
    seattle: {
      deliveriesRemaining: '残り輸送回数',
      selectGoods: '輸送する商品を選択',
      grayCityNote: 'Gray City: 取り除く(+1VP)か留置を選択',
      grayCityTitle: 'Gray Cityへの輸送',
      grayCityPrompt: '商品をどうしますか？',
      deliver: '取り除く',
      deliverDesc: 'ゲームから除去、終了時+1VP',
      keep: '留置する',
      keepDescAirport: 'Airportに留置',
      keepDescDowntown: 'Downtownに留置（無制限）',
      airportFull: 'Airportは満杯です (3/3)',
      specialRulesTitle: '特別ルール',
      specialRule1: 'Ph.9注意: Airportに自動で1個補充（最大3個）。ダイス3個を振り、3個のキューブを手動配置（各場所最大2個）。配置しきれないと即敗北。',
      specialRule2: '白色都市（Downtown, Airport）: 商品キューブを一時的に留置し、後で他の都市へ輸送可能。',
    },
  },

  refillGoods: {
    title: '生産',
    productionSelected: '生産アクション選択中',
    drawnGoods: '引いた商品:',
    openGoodsDisplay: '商品ディスプレイを開く',
    rollingDice: 'ダイスを振っています...',
    rolled: 'ダイス目: {dice1}, {dice2}',
    noGoodsPlaced: '商品は配置されませんでした',
    clickToProceed: '完了ボタンで次へ',
    seattle: {
      diceResults: 'ダイス結果',
      airportAuto: 'Airport自動配置',
      airportFull: 'Airportが満杯です！多くの乗客が立ち往生しています。',
      demandOverload: '需要過多！',
      selectGoods: '配置する商品を選択',
      selectLocation: '配置先を選択',
      clickMapToPlace: 'マップ上のハイライトされた City/Town をクリック',
      selectedLocation: '選択中',
      noAvailableLocation: '配置可能な場所がありません - 需要過多!',
      allPlaced: '全ての商品を配置しました!',
      proceed: '次へ',
      gameOver: 'ゲームオーバー',
      specialRulesTitle: '特別ルール',
      specialRule1: 'フェーズ開始時にAirportへ自動で1個補充（最大3個、満杯でゲームオーバー）',
      specialRule2: 'ダイス3個を振り、出目に対応するエリアに3個のキューブを配置（各場所最大2個）',
      specialRule3: '3個目を配置すると即ゲームオーバー（需要過多）',
    },
  },

  autoPhase: {
    title: '自動処理',
    processing: '処理中...',
    clickToProceed: 'クリックして次のフェーズへ',
  },

  goodsDisplay: {
    title: '商品ディスプレイ',
    production: '生産アクション',
    lightSide: 'ライトサイド',
    darkSide: 'ダークサイド',
    legend: '凡例',
    colors: {
      red: '赤',
      blue: '青',
      yellow: '黄',
      black: '黒',
      purple: '紫',
    },
    selectCube: 'キューブを選択',
    selectCell: '空きマスをクリック',
    placed: '{count}/2 配置済',
    selected: '選択中',
  },

  // ============================================================================
  // Game Start Modal - 共通キー
  // ============================================================================
  gameStartCommon: {
    // ヘッダー
    title: '蒸気の時代',
    subtitle: 'ソロマップ ルール説明',
    designedBy: 'デザイナー:',

    // 概要（マップ固有の description は各マップで定義）
    description: '',

    // 勝利/終了条件セクション
    victoryCondition: '勝利条件',
    gameEnd: 'ゲームは10ターン後に終了',
    loseCondition: '敗北',

    // 特殊ルールセクション
    specialRules: '特別ルール',
    phase: 'Ph.{num}',

    // 戦略ヒント
    strategyTips: '戦略ヒント',
    tip1: '',
    tip2: '',
    tip3: '',
    tip4: '',

    // ボタン
    startGame: 'ゲーム開始',
    backToMaps: 'マップ選択に戻る',
    returnToGame: 'ゲームに戻る',
  },

  // ============================================================================
  // Game Start Modal - マップ固有キー
  // ============================================================================
  gameStartBarbados: {
    // マップ固有の概要・条件
    description: 'カリブ海に浮かぶバルバドス島が舞台。砂糖農園から収穫されたパイナップル（黄色キューブ）を島内の都市へ輸送し、ゲーム終了時に破産せずに株を買い戻すことが目標となります。',
    gameEnd: 'ゲームは10ターン後に終了',
    loseCondition: '全株式を買い戻せない場合は敗北！',
    tip1: 'アクション選択は慎重に - 各アクションは4ターンに1回のみ利用可能',
    tip2: '株式発行のバランスを取る - 現金が必要だが$5で買い戻す必要あり',
    tip3: '線路建設より収入を重視 - 現金が最終スコア',
    tip4: '中央の山岳地帯に効率的に線路敷設',

    // Phase ルール説明
    issueSharesRule: '株式発行:',
    issueSharesDesc: '1ターンに1株のみ',
    selectActionsRule: 'アクション選択:',
    selectActionsDesc: '4アクションのみ -',
    selectActionsActions: 'エンジニア, 機関車, 都市化, 商品補充',
    refillGoodsRule: '商品補充:',
    refillGoodsDesc: 'ライトサイドのみ2個のダイスを振る',

    // 勝利条件詳細
    buyBackShares: '全ての株式を$5で買い戻す',
    remainingCash: '残りの現金が最終スコア',

    // 特殊ルール詳細
    selectActionsMarker: '各アクションは選択時にマーカーが置かれます。',
    selectActionsCannotReselect: '4つ全てにマーカーが置かれるまで再選択不可。',
    selectActionsReset: 'その後マーカーがリセットされ、全て利用可能に。',
  },

  gameStartPuertoRico: {
    // マップ固有の概要・条件
    description: 'カリブ海プエルト・リコ島で赤と黒のキューブをサン・ファンに輸送します。未輸送の黒キューブはペナルティになるのでご注意を。',
    gameEnd: 'ゲームは10ターン後に終了',
    loseCondition: 'ペナルティ後の収入が0以下で敗北',
    tip1: '黒キューブの輸送を優先 - 各-10のペナルティになる',
    tip2: 'アクション選択は任意 - 不要な時は$5を節約',
    tip3: '全ての町からサン・ファンへの効率的なルートを構築',
    tip4: '機関車強化と線路建設コストのバランスを取る',

    // Phase ルール説明
    selectActionsRule: 'アクション選択:',
    selectActionsDesc: '2アクションのみ -',
    selectActionsActions: 'エンジニア, 機関車',
    refillGoodsRule: '商品補充:',
    refillGoodsDesc: 'スキップ（商品補充なし）',

    // 勝利条件詳細
    blackCubePenalty: '残った黒キューブ1個につき収入-10',
    remainingScore: '最終スコア = (収入 × 3) + リンク数 - (株式数 × 3)',

    // 特殊ルール詳細
    selectActionsCost: 'アクション選択には$5必要',
    selectActionsOptional: 'アクション選択は任意（スキップ可能）',

    // 難易度選択
    difficultyLabel: '難易度',
    difficultyDesc: '難易度を選択（赤/黒キューブの割合が変わります）',
    difficulties: {
      Beginner: '入門',
      Novice: '初級',
      Expert: '中級',
      Master: '上級',
      Conqueror: '達人',
      God: '神',
    },
    cubeRatio: '赤: {red} / 黒: {black}',
    difficultyChangeNotice: '※ 難易度が変更されました。開始時にゲームが再設定されます。',
  },

  gameStartDetroit: {
    // マップ固有の概要・条件
    description: 'このマップは2000年代半ばのデトロイトをモデルにしています。決められたターン数プレイする代わりに、プレイヤーが破産するまでゲームを続けます。あなたの目標は、6ターン目終了時に破産する仮想政府に勝つことです。仮想政府よりも長く生き残れば勝利です。',
    gameEnd: 'ゲームは破産まで継続',
    loseCondition: '7ターン未満で破産 = 敗北',
    tip1: '初期現金$0 - まず株式発行で資金調達',
    tip2: '各アクションに$1-$3の手数料 - 使い時を慎重に判断',
    tip3: '維持費は毎ターン増加 - 素早く収入を確保',
    tip4: '高額なリンク形成はエンジニアの無料枠で',

    // Phase ルール説明
    issueSharesRule: '株式発行:',
    issueSharesDesc: '最大25株まで発行可能（初期5株発行済みだが初期資金は$0）',
    selectActionsRule: 'アクション選択:',
    selectActionsDesc: '3アクションのみ -',
    selectActionsActions: 'エンジニア, 機関車, 都市化',
    payExpensesRule: '経費支払:',
    payExpensesDesc: '維持費にターン数×$1が追加（毎ターン増加）',
    incomeReductionRule: '収入減少:',
    incomeReductionDesc: '5ステップ制の収入減少（通常より速い）',
    refillGoodsRule: '商品補充:',
    refillGoodsDesc: '両サイド2個のダイスを振る',

    // 勝利条件詳細
    scoreIsTurns: 'スコア = 生き残ったターン数',
    winCondition: '7ターン以上生存 = 勝利',

    // 特殊ルール詳細
    diskSystem: '各アクションにディスク3枚（使用上限: 各3回まで）',
    diskFee: '手数料: $3（初回）→ $2 → $1（最終回）',

    // 特殊リンク
    specialLinksTitle: '特殊リンク/ヘクス',
    ferryLinkDesc: 'フェリーリンク: 離れたヘクス同士を1リンクで直接結ぶ仮想線路',
    adjustLincDesc: '調整リンク: 隣接ヘクス同士を1リンクで直接結ぶ仮想線路',
  },

  gameStartSeattle: {
    // マップ固有の概要・条件
    description: 'このマップでは商品キューブはシアトルの交通利用者を表します。アクション選択の後払いコストに注意しつつ、都市間を支える交通網を構築し、破産せずに増え続ける乗客へ対応することが目標です。機関車アクションは、商品輸送の回数を増加させることも出来ます。',
    gameEnd: 'ゲームは10ターン後に終了',
    loseCondition: '最終スコアがマイナス / 清算失敗 = 敗北',
    tip1: '貨物レベル強化と維持費のバランスを取る',
    tip2: 'Downtown/Airportを難しい色の一時保管場所として使用',
    tip3: 'Ph.5の需要過多警告に注意',
    tip4: 'アクション使用を最小限にして清算費用を削減',

    // Phase ルール説明
    selectActionsRule: 'アクション選択:',
    selectActionsDesc: '2アクションのみ（いずれか/両方/何もしない） -',
    selectActionsActions: 'エンジニア, 機関車',
    locomotiveChoice: '機関車: 機関車+1 または 貨物輸送+1 を選択',
    actionTracking: '使用回数は清算時に$5/回の支払いが必要',
    moveGoodsRule: '商品輸送:',
    moveGoodsDesc: '貨物Lv.回の輸送（初期: 2、最大: 5）',
    grayCities: 'Downtown/Airport: 何色でもOK、収入+0',
    deliverOrKeep: '取り除く(終了時得点)か留置して後で輸送するか選択',
    payExpensesDesc: '追加維持費: 貨物Lv.維持費（Lv.3=$4、Lv.4=$7、Lv.5=$10）',
    refillGoodsDesc: 'Airport自動補充（毎ターン1個、最大3個まで）、ランダムな3個を対応エリアに手動配置（各場所最大2個まで）',
    demandOverload: '配置上限を超過すると即敗北',

    // 勝利条件詳細
    winCondition: '最終スコア 0 以上 = 勝利',
    settlementPayment: '清算: $5 × 株式数 + $5 × アクション使用回数',
    scoreCalculation: 'スコア: （Downtown/Airport 輸送済みキューブ数） - （マップ上の残留キューブ数） - （Downtown 未接続都市/町の数）',

    // 特殊リンク
    specialLinksTitle: '特殊リンク/ヘクス',
    preLaidTrack: '敷設済み線路: Downtown-Airport 間は接続済み（所有者なし）',
    wallsDesc: 'マップ上の赤線 = 壁（通行不可）',
    mountainDesc: '山岳ヘクスにはタイル配置不可',
  },

  gameEnd: {
    victory: '勝利！',
    defeat: '敗北',
    bankruptcy: '破産',
    bankruptcyMessage: '資金がなくなりました… 次回はコスト管理を慎重にお願いします',
    playAgain: 'もう一度プレイ',
    viewMap: '最終局面を見る',
    viewScore: '結果を見る',
    finalScore: '最終スコア',
    turnsPlayed: 'プレイターン',
    linksCompleted: '完成リンク数',
    sharesBuyback: '株式買戻し',
    // 統計ラベル
    statsTitle: 'ゲーム統計',
    turn: 'ターン',
    income: '収入',
    links: 'リンク',
    loco: '機関車Lv.',
    // スコア計算ラベル
    cashLabel: '所持金',
    buybackCost: '買戻し',
    scoreLabel: 'スコア',
    detroit: {
      evaluations: {
        turn12: 'あなたは Age of Steam の殿堂入りです。写真を撮り、日付とサインを入れて、大切に保管してください。',
        turn11: '11！',
        turn10: '工場から車が次々と出荷されます。デトロイト・ロック・シティがあなたに敬意を表します。',
        turn9: 'エキスパートレベルに進みました。デトロイトの経済は好転し始めています。',
        turn8: '平均以上のスキルを習得しました。デトロイトはあなたの努力を称賛します。',
        turn7: '政府よりも優れています！素晴らしい！',
        defeat: '政府より先に破産してしまいました。再挑戦しましょう！',
      },
    },
    barbados: {
      evaluations: {
        cash31: '傑出した成績！カリブ海の貿易王です。',
        cash21: '素晴らしい！パイナップル帝国は繁栄しています。',
        cash11: 'よくできました！有能な商人であることを証明しました。',
        cash1: 'お見事！利益を出すことができました。',
        cash0: 'ギリギリ生存！全株式を買い戻しましたが余裕はありません。',
        defeat: '全株式を買い戻せませんでした。次回頑張りましょう！',
      },
    },
    puertoRico: {
      evaluations: {
        score61: '伝説的！プエルトリコを制覇しました。',
        score41: '素晴らしい！島はあなたのおかげで繁栄しています。',
        score21: 'よくできました！堅実な成績です。',
        score1: 'お見事！成功を収めました。',
        score0: 'ギリギリ生存！なんとか間に合いました。',
        defeat: '黒キューブに圧倒されました。再挑戦してください！',
      },
    },
    seattle: {
      demandOverloadTitle: '需要過多',
      demandOverload: '交通需要が許容量を超えました！',
      settlementFailedTitle: '清算失敗',
      settlementFailed: '清算失敗！',
      settlement: '清算',
      shares: '株式',
      actions: 'アクション',
      total: '合計',
      cash: '所持金',
      score: 'スコア計算',
      scoreFormula: 'スコア = 輸送済 − 残存 − 未接続',
      delivered: '輸送済キューブ (Downtown/Airport)',
      remaining: 'マップ上の残存キューブ',
      unconnected: '未接続の都市/町',
      finalScore: '最終スコア',
      evaluations: {
        excellent: '素晴らしい！シアトルの交通網は世界最高水準です！',
        good: 'よくやりました！都市は十分に接続されています。',
        passing: 'なんとか達成！シアトルには基本的な交通網ができました。',
        poor: '交通システムの改善が必要です...',
        veryPoor: 'シアトルにはもっと良いリーダーが必要です。',
      },
    },
  },

  settings: {
    title: '設定',
    language: '言語',
    languageEnglish: 'English',
    languageJapanese: '日本語',
    close: '閉じる',
    // 追加機能
    showRules: 'ルール説明',
    resetGame: 'ゲームをやめる',
    resetGameConfirm: 'マップ選択画面に戻りますか？',
    undoToTurnStart: 'ターン開始時に戻る',
    undoToTurnStartConfirm: 'このターンの操作をすべて取り消しますか？',
  },

  mapSelection: {
    title: '蒸気の時代',
    subtitle: 'プレイするマップを選択',
    comingSoon: '準備中',
    players: 'プレイヤー:',
    solo: 'ソロ',
    selectDifficulty: '難易度を選択',
    redCubes: '赤:',
    blackCubes: '黒:',
    cancel: 'キャンセル',
    play: 'プレイ',
    mapEditor: 'マップエディター',
    deviceRecommendation: 'PC推奨。スマホの場合は横持ちで遊んでください。',
    hallOfHonor: '殿堂',
    achievements: '実績',
    statistics: '統計',
    difficulty: '難易度:',
    difficultyStars: {
      easy: '★☆☆',
      medium: '★★☆',
      hard: '★★★',
    },
    maps: {
      barbados: {
        name: 'バルバドス',
        description: 'アクションの選びどころがポイント。Age of Steam の入門に最適。',
      },
      puertoRico: {
        name: 'プエルトリコ',
        description: '難易度を変えて運試し。',
      },
      detroit: {
        name: 'デトロイト財政破綻',
        description: 'とにかくお金がない！ 破産するまで何ターン生き残れるか挑戦。',
      },
      seattle: {
        name: 'シアトルメトロ',
        description: '破産や需要過多を避けつつ、早めに輸送力を上げ商品を減らしていこう。',
      },
    },
  },

  hallOfHonor: {
    title: '殿堂',
    back: '戻る',
    periodAll: '全期間',
    periodWeekly: '週間',
    periodMonthly: '月間',
    rank: '#',
    player: 'プレイヤー',
    score: 'スコア',
    turn: 'ターン',
    date: '日付',
    watch: '再生',
    noScores: 'まだスコアが登録されていません。',
    prev: '前へ',
    next: '次へ',
    scoresRegistered: '{count}件のスコア',
    loading: '読み込み中...',
    error: 'ランキングの読み込みに失敗しました',
    rank1: '1位',
    rank2: '2位',
    rank3: '3位',
    cash: '現金',
    shares: '株式',
    locoLv: '機関車',
    links: 'リンク',
    cargoLv: '貨物',
    delivered: '輸送済',
    remaining: '残存',
    unconnected: '未接続',
    undoCount: 'やり直し',
    filterAll: '全て',
    filterNoUndo: 'やり直しなし',
  },

  achievements: {
    title: '実績',
    back: '戻る',
    cookieNotice: '実績データはブラウザのローカルストレージに保存されています。サイトデータを消去するとリセットされます。',
    unlocked: '達成済',
    locked: '未達成',
    progress: '{unlocked} / {total} 達成',
    names: {
      // Barbados
      barbados_cash60: 'カリブ海の貿易王',
      barbados_cash40: 'パイナップル帝国',
      barbados_cash20: '有能な商人',
      barbados_first_win: '初心者卒業',
      barbados_shares6: '慎重な商人',
      barbados_shares4: '無借金経営',
      barbados_no_undo: '完璧なプレイ',
      // Detroit
      detroit_turn25: '猫を揉む',
      detroit_turn20: '不屈の精神',
      detroit_turn15: '大統領',
      detroit_turn12: '殿堂入り',
      detroit_turn10: 'ロック・シティ',
      detroit_turn8: '平均以上',
      detroit_no_undo: '一発勝負',
      // Puerto Rico
      puerto_rico_score70: '伝説の総督',
      puerto_rico_score50: '島の繁栄者',
      puerto_rico_score30: '堅実な統治',
      puerto_rico_first_win: '初心者卒業',
      puerto_rico_clean_island: '島の浄化者',
      puerto_rico_cubes3: '効率的な配送',
      puerto_rico_shares13: '節約家の総督',
      puerto_rico_shares10: '清廉な支配者',
      puerto_rico_no_undo: '熟練の航海',
      // Seattle
      seattle_score20: '伝説の都市計画者',
      seattle_score15: '未来都市',
      seattle_score10: '世界最高水準',
      seattle_score5: '平均的交通網',
      seattle_score0: 'なんとか達成',
      seattle_all_connected: '取りこぼしなし',
      seattle_loco5: '省エネ走行',
      seattle_loco3: '蒸気機関の達人',
      seattle_cargo4: '効率的な物流',
      seattle_cargo3: '最小輸送',
      seattle_shares10: '節約家',
      seattle_shares7: '超節約経営',
      seattle_no_undo: '完璧な都市計画',
    },
    descriptions: {
      // Barbados
      barbados_cash60: '最終スコア60以上でクリア',
      barbados_cash40: '最終スコア40以上でクリア',
      barbados_cash20: '最終スコア20以上でクリア',
      barbados_first_win: '破産せずにクリア',
      barbados_shares6: '発行株式数6以下でクリア',
      barbados_shares4: '発行株式数4以下でクリア',
      barbados_no_undo: 'Undoなしでクリア',
      // Detroit
      detroit_turn25: '25ターン生存',
      detroit_turn20: '20ターン生存',
      detroit_turn15: '15ターン生存',
      detroit_turn12: '12ターン生存',
      detroit_turn10: '10ターン以上生存',
      detroit_turn8: '8ターン以上生存',
      detroit_no_undo: 'Undoなしで勝利',
      // Puerto Rico
      puerto_rico_score70: '最終スコア70以上でクリア',
      puerto_rico_score50: '最終スコア50以上でクリア',
      puerto_rico_score30: '最終スコア30以上でクリア',
      puerto_rico_first_win: '黒キューブの災禍を乗り越えてクリア',
      puerto_rico_clean_island: '黒キューブをすべて運んでクリア',
      puerto_rico_cubes3: '残りキューブ合計3個以下でクリア',
      puerto_rico_shares13: '発行株式数13以下でクリア',
      puerto_rico_shares10: '発行株式数10以下でクリア',
      puerto_rico_no_undo: 'Undoなしでクリア',
      // Seattle
      seattle_score20: '最終スコア20以上でクリア',
      seattle_score15: '最終スコア15以上でクリア',
      seattle_score10: '最終スコア10以上でクリア',
      seattle_score5: '最終スコア5以上でクリア',
      seattle_score0: 'スコア0以上でクリア（勝利）',
      seattle_all_connected: 'すべてのCity/Townを接続してクリア',
      seattle_loco5: '機関車レベル5以下でクリア',
      seattle_loco3: '機関車レベル3以下でクリア',
      seattle_cargo4: '貨物輸送レベル4以下でクリア',
      seattle_cargo3: '貨物輸送レベル3以下でクリア',
      seattle_shares10: '発行株式数10以下でクリア',
      seattle_shares7: '発行株式数7以下でクリア',
      seattle_no_undo: 'Undoなしでクリア',
    },
  },

  statistics: {
    title: '統計',
    back: '戻る',
    storageNotice: '統計データはブラウザのローカルストレージに保存されています。サイトデータを消去するとリセットされます。',
    playCount: 'プレイ回数',
    clearCount: 'クリア回数',
    noUndoClearCount: 'やり直しなしクリア',
    bestScore: '最高スコア',
    avgCash: '平均 現金',
    avgShares: '平均 株式数',
    avgLoco: '平均 機関車Lv',
    avgLinks: '平均 リンク数',
    avgCargoLv: '平均 貨物Lv',
    avgDelivered: '平均 輸送済',
    avgRemaining: '平均 残存',
    avgUnconnected: '平均 未接続',
    noData: 'まだデータがありません。ゲームをプレイすると統計が表示されます。',
  },
};
